import * as ACTIONS from '@/store/constants/actions.js';
import * as MUTATIONS from '@/store/constants/mutations.js';
import { getDisabledContentRatio, getTagAgreementRate } from '@/generated/console.js';
import { getDistinct } from '@/util/array.js';
import { isUuid } from 'commons/util/parse';
import * as GETTERS from '@/store/constants/getters.js';
import {
    CLIENT_UUID_FILTER, SITE_UUID_FILTER, SECTION_UUID_FILTER, FILTERS,
} from '@/constants/filters.js';
import { v4 } from 'uuid';
import { translateUuidToId, translateIdToUuid } from '@/util/filters.js';
import { IS_OPERATOR } from '@/constants/filters.js';

export function getUniqueUsers(content) {
    // Get all the users from the content
    const contentUsers = content.map(({
        actor_uuid, author_host_section_uuid, site_uuid, origin,
    }) => {
        const user = {
            actor_uuid,
            site_uuid,
            origin,
        };

        if (author_host_section_uuid !== undefined) {
            user.author_host_section_uuid = author_host_section_uuid;
        }

        return user;
    });

    // Get all the users from the tags
    const tagUsers = content.flatMap(({ tags, site_uuid }) =>
        tags.flatMap((tag) =>
            tag.moderator_id
                .filter((uuid) => uuid && isUuid(uuid))
                .map((actor_uuid) => ({
                    actor_uuid,
                    site_uuid,
                })),
        ),
    );

    return getDistinct(tagUsers.concat(contentUsers), (a, b) => a.actor_uuid === b.actor_uuid);
}

export function fetchUsers(uniqueUsers, dispatch) {
    return uniqueUsers.map(({
        actor_uuid, site_uuid, author_host_section_uuid, origin,
    }) => dispatch(ACTIONS.FETCH_USER_BY_UUID, {
        uuid: actor_uuid,
        site_uuid,
        author_host_section_uuid,
        origin,
    }));
}

export function fetchDisabledContent(uniqueUsers) {
    return getDisabledContentRatio({
        actors: {
            actor_list: uniqueUsers.map(({ actor_uuid }) => actor_uuid),
        },
    });
}

// export const fetchDisabledContent = (uniqueUsers) => getDisabledContentRatio({ actors: { actor_list: uniqueUsers.map(({ actor_uuid }) => actor_uuid) } });

export function commitDisabledRatios(disabled_content_ratios, commit) {
    disabled_content_ratios.forEach((ratio) => {
        commit(MUTATIONS.SET_USER, {
            uuid: ratio.actor_uuid,
            user: {
                disabled_content_ratio: ratio.percent_disabled,
            },
        });
    });

    return Promise.resolve();
}

export function fetchAgreementRate(uniqueUsers) {
    return getTagAgreementRate({
        actors: {
            actor_list: uniqueUsers.map(({ actor_uuid }) => actor_uuid),
        },
    });
}

// export const fetchAgreementRate = (uniqueUsers) => getTagAgreementRate({ actors: { actor_list: uniqueUsers.map(({ actor_uuid }) => actor_uuid) } });

export function commitAgreementRate(tag_agreement_rates, commit) {
    tag_agreement_rates.forEach((rate) => {
        commit(MUTATIONS.SET_USER, {
            uuid: rate.user_uuid,
            user: {
                tag_agreement_rate: rate.tag_agreement_rate,
                total_community_tags: rate.total_community_tags,
            },
        });
    });
    return Promise.resolve();
}

function formatFilter(data) {
    // Keep a uuid so we can differentiate filters that are the same field
    const filter = Object.assign({}, data, { filter_uuid: v4() });

    if (filter.field === CLIENT_UUID_FILTER || filter.section_filter) {
        filter.section_filter = Object.assign({
            field: SECTION_UUID_FILTER,
            values: [],
            operator: 'equals',
        }, filter.section_filter);
    }

    return filter;
}

/**
 * @param {Array} sites - list of site uuids
 * @param {Object} getters - Vuex getters
 * @returns {Object} returns an object containing client uuids mapped to the sites that are inside that client
 */
const getSitesByClient = (sites, getters) => sites.reduce((acc, site_uuid) => {
    const site = getters[GETTERS.SITE_BY_UUID](site_uuid);
    const client_uuid = site.client_uuid;

    if (!acc[client_uuid]) {
        acc[client_uuid] = {
            uuid: client_uuid,
            sites: [ site_uuid ],
        };
    }
    else {
        acc[client_uuid].sites.push(site_uuid);
    }
    return acc;
}, {});

const EMPTY_CLIENT_FILTER = {
    field: CLIENT_UUID_FILTER,
    key: CLIENT_UUID_FILTER,
    client_uuid: undefined, // doesn't really matter what the client_uuid is
    values: [],
};

/**
 * Formats the filters returned from the API
 * 1) Add local filter uuid to filters
 * 2) Split all clients and sites (into client filters. This also means there should no longer be any "site filters" in console front)
 * 3) Translate uuids -> ids
 * 4) Remove sites/clients that are not accessible by the user
 * 5) Remove empty filters (if there are any for whatever reason)
 * @param {Array} filters - List of filters on a saved search
 * @param {Object} getters - Vuex store getters
 *
 * @returns {Array} A list of filters without any clients or sites that do not exist, and has each client/site split out as its own filter
 */
export function formatFiltersFromApi(filters = [], getters) {
    /* eslint no-loop-func: off */

    let result = [];

    for (const filter of filters) {
        if (filter.field === CLIENT_UUID_FILTER) {
            // HACK: Remove all unauthorized clients from the client filter
            const filterValues = filter.values.filter((client_uuid) => getters[GETTERS.CLIENT_BY_UUID](client_uuid).client_uuid);
            // If there are no filters left, that means they were all unauthorized and we want to keep an empty filter here so we know to filter this queue/search out.
            // This is different from a queue/search which intentionally has no clients selected on it which should be shown to all users
            if (filterValues.length === 0) {
                result.push(formatFilter(EMPTY_CLIENT_FILTER));
            }
            else {
                // If it is a client filter, add all the clients as individual filters with all sites selected in each
                // so that we can display each client as an individual tag
                // We need to filter out filters where both the client and the site were given
                // So only add the client if we don't already have it in the array
                const clientFilters = filterValues.filter((client_uuid) => !result.find((other) => other.client_uuid === client_uuid))
                    .map((client_uuid) => formatFilter({
                        field: CLIENT_UUID_FILTER,
                        key: CLIENT_UUID_FILTER,
                        client_uuid,
                        values: getters[GETTERS.SITES_PER_CLIENT](client_uuid),
                    }));

                result = result.concat(clientFilters);
            }
        }
        else if (filter.field === SITE_UUID_FILTER) {
            // HACK: We want to remove all unauthorized sites from the site filter
            const filterValues = filter.values.filter((site_uuid) => getters[GETTERS.SITE_BY_UUID](site_uuid));

            // HACK:
            // If there are no filters left, that means they were all unauthorized and we want to keep an empty filter here so we know to filter this queue/search out.
            // This is different from a queue/search which intentionally has no clients selected on it which should be shown to all users
            if (filterValues.length === 0) {
                result.push(formatFilter(EMPTY_CLIENT_FILTER));
            }
            else {
                /**
                 * HACK: the api response aggregates all site filters under a single field name "site_uuid"
                 * the following code unwinds all site uuids to identify to which client they belong and regroup them
                 * after the proper client->sites have been related each group is added as an individual filter
                 {
                    "field": "site_uuid",
                    "values": [
                        "00000000-0000-4000-8000-3ac9a3502d53",
                        "00000000-0000-4000-8000-2daa4d022d7f",
                        "00000000-0000-4000-8000-000000000bc8"
                    ],
                    "operator": "equals"
                }
                 */

                // If it is a site filter, create a client filter with the selected sites in it
                // so that we can display each site as an individual tag
                const sitesByClient = getSitesByClient(filterValues, getters);

                Object.values(sitesByClient).forEach((client) => {
                    // We need to filter out filters where both the client and the site were given
                    // So if the site is given, just remove all filters with the same client uuid
                    result = result.filter((other) => other.client_uuid !== client.uuid);

                    result.push(formatFilter({
                        field: CLIENT_UUID_FILTER, // Let's just keep both as client uuid filters
                        key: CLIENT_UUID_FILTER,
                        client_uuid: client.uuid,
                        values: client.sites,
                    }));
                });
            }
        }
        else if (filter.field === FILTERS.actor_uuid.field || filter.field === FILTERS.moderator_uuid.field) {
            // If the field is actor uuid or moderator uuid, we want to convert it to an ID again
            result.push(
                formatFilter({
                    ...filter,
                    values: filter.values.map(translateUuidToId),
                }),
            );
        }
        else if (filter.field === FILTERS.moderator_actions.field) {
            const [ moderatorId, ...actions ] = filter.values; // moderator id is always the first value

            result.push(
                formatFilter({
                    ...filter,
                    values: [ translateUuidToId(moderatorId) ].concat(actions),
                }),
            );
        }
        else {
            result.push(formatFilter(filter));
        }
    }

    /**
    * HACK: TCM-979, TCM-1096
    * The backend API is not able to return a single client_uuid filter containing
    * the list of sites and a separate list of sections if any was selected.
    */
    // Create a key-value pair that matches section uuid to their corresponding sites to make further searches faster.
    const sitesBySectionUuid = {};
    const sites = Object.values(getters[GETTERS.ALL_SITES] || {}); // fallback empty object to prevent unit test errors
    for (const site of sites) {
        const sections = site.sections || []; // failsafe in case sections is not defined

        for (const section of sections) {
            sitesBySectionUuid[section.uuid] = site;
        }
    }

    // Find all section_uuid filters, identify to which site it belongs to and attach to its corresponding client_uuid filter
    const sectionFilters = filters.filter((filter) => filter.field === SECTION_UUID_FILTER);
    for (const sectionFilter of sectionFilters) {
        // take only the first section uuid as sample since all sections belong to the same site.
        const sectionUuid = sectionFilter.values[0];
        const site = sitesBySectionUuid[sectionUuid];

        result.forEach((filter) => {
            if (filter.field === CLIENT_UUID_FILTER && filter.values.includes(site.uuid)) {
                filter.section_filter = sectionFilter;
            }
        });
    }
    /**
    * END HACK
    */

    // We want to remove all filters that have no values UNLESS they are a client or site filter.
    // In that case, we want to keep the empty values list to know that the filter existed
    // and all the clients/sites in it are unavailable to the user
    result = result.filter((filter) => (filter.values.length > 0 || filter.field === CLIENT_UUID_FILTER) && filter.field !== SECTION_UUID_FILTER);

    return result;
}

/**
 * Formats the filters for sending to the API
 * 1) Delete filter uuid and key and other unneeded api things
 * 2) Map values -> value.key (or in some rare cases where it isn't an object, keep it as is. TODO lets try to keep it consistent)
 * 3) Aggregate all clients and sites
 * 4) Translate ids -> uuids
 * 5) Remove empty filters
 * @param {Array} filters - Array of non formatted filters
 * @returns {Array} Array of formatted filters
 */
export const formatFiltersForApi = (filters) => filters.reduce((acc, filter) => {
    // Client filter encompasses both client and site filters
    if (filter.field === CLIENT_UUID_FILTER) {
        const filterIndex = acc.findIndex((f) => f.field === SITE_UUID_FILTER);

        // if the site filter already exists, add this site to it
        if (filterIndex >= 0) {
            acc[filterIndex].values = acc[filterIndex].values.concat(filter.values);
        }
        else {
        // Otherwise lets initialize it
            acc.push({
                field: SITE_UUID_FILTER,
                operator: IS_OPERATOR,
                values: filter.values,
            });
        }

        if (filter.section_filter && filter.section_filter.values) {
            acc.push(filter.section_filter);
        }
    }

    // Sigh add a special condition here for actor uuid and moderator uuid to convert it to a uuid
    else if (filter.field === FILTERS.actor_uuid.field || filter.field === FILTERS.moderator_uuid.field || filter.field === FILTERS.moderator_actions.field) {
        acc.push({
            field: filter.field,
            operator: filter.operator,
            values: filter.values.map((value) => translateIdToUuid(value)),
        });
    }
    else {
        acc.push({
            field: filter.field,
            operator: filter.operator,
            values: filter.values,
        });
    }

    return acc;
}, [])
    .filter((filter) => filter.values.length > 0);
